/**
 * DeedStack Theme Configuration
 *
 * This file defines all brand colors and theme variables.
 * These values are applied as CSS custom properties by ConfigContext
 * when the app loads configuration from the server.
 *
 * To customize colors for a white-label deployment:
 * 1. Update siteConfig.js on the server with your brand colors
 * 2. The colors will be fetched and applied automatically
 *
 * For local development/testing, these defaults are used.
 */

// Default theme colors (fallbacks if server config unavailable)
const theme = {
  // ===========================================
  // Primary Colors - Main brand identity (Navy Blue)
  // ===========================================
  colors: {
    // Primary brand color (headers, nav, buttons)
    primary: '#1e3a5f',
    primaryDark: '#152a45',
    primaryLight: '#2a4a7d',

    // Secondary/accent color (CTAs, highlights) - Gold
    secondary: '#c9a227',
    secondaryDark: '#a68617',
    secondaryLight: '#d4af37',

    // Accent color (links, interactive elements) - Gold
    accent: '#c9a227',
    accentDark: '#a68617',
    accentLight: '#d4af37',

    // ===========================================
    // Semantic Colors - Status and feedback
    // ===========================================
    success: '#28a745',
    successLight: '#d4edda',

    warning: '#ffc107',
    warningLight: '#fff3cd',

    error: '#dc3545',
    errorLight: '#f8d7da',

    info: '#17a2b8',
    infoLight: '#d1ecf1',

    // ===========================================
    // Neutral Colors - Text and backgrounds
    // ===========================================
    text: '#333333',
    textLight: '#666666',
    textMuted: '#999999',

    // These stay literal on purpose. This object is the SOURCE of the palette:
    // applyThemeColors writes these values into the CSS custom properties, so
    // expressing one as var(--color-border) would define a variable in terms of
    // itself, which is invalid at computed-value time and leaves it unset. A
    // literal-to-variable sweep must skip this file.
    background: '#ffffff',
    backgroundAlt: '#f8f9fa',
    backgroundLight: '#f0f4f8',  // Light tint of primary (navy)

    border: '#e0e0e0',
    borderLight: '#f0f0f0',

    // ===========================================
    // Special Colors
    // ===========================================
    white: '#ffffff',
    black: '#000000',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },

  // ===========================================
  // Typography
  // ===========================================
  fonts: {
    primary: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    heading: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    monospace: "'Consolas', 'Monaco', monospace",
  },

  // ===========================================
  // Spacing & Layout
  // ===========================================
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },

  // ===========================================
  // Border Radius
  // ===========================================
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    round: '50%',
  },

  // ===========================================
  // Shadows
  // ===========================================
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 2px 8px rgba(0, 0, 0, 0.1)',
    lg: '0 4px 16px rgba(0, 0, 0, 0.15)',
  },

  // ===========================================
  // Breakpoints (for responsive design)
  // ===========================================
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1280px',
  },
};

/**
 * Apply theme colors as CSS custom properties
 * Called by ConfigContext after fetching server config
 */
export const applyThemeColors = (colors) => {
  const root = document.documentElement;

  // Primary colors
  if (colors.primaryColor) root.style.setProperty('--color-primary', colors.primaryColor);
  if (colors.primaryDark) root.style.setProperty('--color-primary-dark', colors.primaryDark);
  if (colors.primaryLight) root.style.setProperty('--color-primary-light', colors.primaryLight || colors.primaryColor);

  // Secondary colors
  if (colors.secondaryColor) root.style.setProperty('--color-secondary', colors.secondaryColor);
  if (colors.secondaryMuted) root.style.setProperty('--color-secondary-dark', colors.secondaryMuted);

  // Accent colors
  if (colors.accentColor) root.style.setProperty('--color-accent', colors.accentColor);

  // Semantic colors
  if (colors.successColor) root.style.setProperty('--color-success', colors.successColor);
  if (colors.warningColor) root.style.setProperty('--color-warning', colors.warningColor);
  if (colors.dangerColor) root.style.setProperty('--color-error', colors.dangerColor);
  if (colors.infoColor) root.style.setProperty('--color-info', colors.infoColor);

  // Background colors
  if (colors.lightBackground) root.style.setProperty('--color-background-light', colors.lightBackground);
  if (colors.borderColor) root.style.setProperty('--color-border', colors.borderColor);

  // Legacy variable aliases (for backward compatibility)
  if (colors.primaryColor) {
    root.style.setProperty('--primary-color', colors.primaryColor);
    root.style.setProperty('--forest-green', colors.primaryColor);
  }
  if (colors.primaryDark) {
    root.style.setProperty('--primary-dark', colors.primaryDark);
    root.style.setProperty('--dark-forest', colors.primaryDark);
  }
  if (colors.secondaryColor) {
    root.style.setProperty('--secondary-color', colors.secondaryColor);
    root.style.setProperty('--sandy-gold', colors.secondaryColor);
  }
  if (colors.accentColor) {
    root.style.setProperty('--accent-color', colors.accentColor);
  }
};

/**
 * Get CSS variable value
 */
export const getCSSVariable = (name) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

/**
 * Default color palette for components that need direct access
 */
export const defaultColors = theme.colors;

export default theme;
